/**
 * BUG-686: wording for a build pick the backend SKIPPED before planning because it
 * cannot be planned at all — already on a route, deleted, done with, or gone.
 *
 * The backend (`screen_build_picks` in `routes/services.py`) reports each such pick
 * with a reason code and a small `detail`:
 *
 * | reason           | detail                                  |
 * |------------------|-----------------------------------------|
 * | `already_routed` | `{route_id, route_name, route_date}`    |
 * | `order_archived` | `{status: "archived"}`                  |
 * | `order_terminal` | `{status: visited/confirmed/rejected/skipped}` |
 * | `order_missing`  | none                                    |
 *
 * The same picks reach two surfaces: the `unassigned` breakdown of a build that
 * created something (toast, bell, routes-list alert — via
 * `describeUnassignedDetail`), and the `params.orders` of the
 * `ROUTE_ORDER_NOT_ELIGIBLE` refusal when NOTHING was plannable (via
 * `apiErrors.ts`). Both word a pick through this module, so the two cannot drift.
 *
 * Lives in `lib/` (not in the routes feature) because `apiErrors.ts` needs it and a
 * `lib` module must not import from a feature slice.
 */

import type { TranslateFn } from "@/lib/apiErrors";
import { formatDayMonthSmartYear } from "@/lib/format";

/** The four reasons a pick is skipped before planning. */
export const INELIGIBLE_PICK_REASONS = [
  "already_routed",
  "order_archived",
  "order_terminal",
  "order_missing",
] as const;

export type IneligiblePickReason = (typeof INELIGIBLE_PICK_REASONS)[number];

export function isIneligiblePickReason(
  value: string,
): value is IneligiblePickReason {
  return (INELIGIBLE_PICK_REASONS as readonly string[]).includes(value);
}

/**
 * Terminal order statuses with an `orders.status.*` label. Enumerated because the
 * key is built dynamically: an unknown status must render no detail at all rather
 * than a raw key path.
 */
const TERMINAL_STATUSES = new Set([
  "visited",
  "confirmed",
  "rejected",
  "skipped",
]);

/** The route an `already_routed` pick sits on, as the backend described it. */
export interface PickRoute {
  id: number | null;
  name: string | null;
  /** ISO date `YYYY-MM-DD`, or null. */
  date: string | null;
}

/** Reads the route of an `already_routed` detail, or `null` when there is none. */
export function readPickRoute(
  detail: Record<string, unknown> | null | undefined,
): PickRoute | null {
  if (!detail) return null;
  const rawId = detail.route_id;
  const id =
    typeof rawId === "number" && Number.isFinite(rawId)
      ? rawId
      : typeof rawId === "string" && /^\d+$/.test(rawId)
        ? Number(rawId)
        : null;
  const name =
    typeof detail.route_name === "string" && detail.route_name.trim()
      ? detail.route_name.trim()
      : null;
  const date =
    typeof detail.route_date === "string" &&
    /^\d{4}-\d{2}-\d{2}/.test(detail.route_date)
      ? detail.route_date
      : null;
  if (id === null && name === null) return null;
  return { id, name, date };
}

/** «Route VW Crafter #3» — or «№16860» for a route with no name. */
export function pickRouteName(route: PickRoute, t: TranslateFn): string {
  return (
    route.name ??
    t("routes.buildAlert.details.routeNumber", { id: route.id ?? "" })
  );
}

/** «Route VW Crafter #3, 20 окт.» — the route and its date, for a list. */
export function pickRouteLabel(route: PickRoute, t: TranslateFn): string {
  const name = pickRouteName(route, t);
  return route.date ? `${name}, ${formatDayMonthSmartYear(route.date)}` : name;
}

/** The translated label of a terminal status, or `null` for an unknown one. */
export function terminalStatusLabel(
  detail: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): string | null {
  const status = detail?.status;
  if (typeof status !== "string" || !TERMINAL_STATUSES.has(status)) return null;
  return t(`orders.status.${status}`);
}

/**
 * The worded cause of one skipped pick
 * («Заказ уже стоит в маршруте «Route VW Crafter #3» на 20 окт.»), or `null` when
 * the reason carries no detail worth a sentence (`order_archived`,
 * `order_missing` — their heading already says everything) or `detail` has an
 * unrecognized shape.
 */
export function describeIneligiblePickDetail(
  reason: IneligiblePickReason,
  detail: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): string | null {
  switch (reason) {
    case "already_routed": {
      const route = readPickRoute(detail);
      if (!route) return null;
      const name = pickRouteName(route, t);
      return route.date
        ? t("routes.buildAlert.details.alreadyRouted", {
            route: name,
            date: formatDayMonthSmartYear(route.date),
          })
        : t("routes.buildAlert.details.alreadyRoutedNoDate", { route: name });
    }
    case "order_terminal": {
      const status = terminalStatusLabel(detail, t);
      return status
        ? t("routes.buildAlert.details.orderTerminal", { status })
        : null;
    }
    case "order_archived":
    case "order_missing":
      return null;
  }
}

/** One pick of a `ROUTE_ORDER_NOT_ELIGIBLE` refusal's `params.orders`. */
interface RefusedPick {
  reason: string;
  detail: Record<string, unknown> | null;
}

function readRefusedPicks(raw: unknown): RefusedPick[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.reason !== "string") return [];
    const detail =
      typeof record.detail === "object" &&
      record.detail !== null &&
      !Array.isArray(record.detail)
        ? (record.detail as Record<string, unknown>)
        : null;
    return [{ reason: record.reason, detail }];
  });
}

/** How many distinct routes the refusal names before «…». */
const MAX_NAMED_ROUTES = 3;

/**
 * Interpolation params for the `ROUTE_ORDER_NOT_ELIGIBLE` refusal — a build none of
 * whose picks can be planned. The backend sends `params: {count, orders}` with the
 * first 10 picks and their reasons; this composes
 * «Ни один из 3 выбранных заказов нельзя включить в маршрут: 2 уже в маршрутах
 * (Route VW Crafter #3, 20 окт.), 1 не найден. …».
 *
 * Returns `{}` (→ the figure-less base sentence) when the params are absent or
 * carry no recognizable pick — never «undefined», never a confident 0.
 */
export function buildIneligiblePicksParams(
  params: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): Record<string, unknown> {
  if (!params) return {};
  const picks = readRefusedPicks(params.orders);
  const rawCount = Number(params.count);
  const count = Number.isInteger(rawCount) && rawCount > 0 ? rawCount : null;
  if (picks.length === 0 || count === null) return {};

  // ONE pick: its own sentence — heading or measured cause, then its advice.
  if (count === 1 && picks.length === 1) {
    const [pick] = picks;
    if (!isIneligiblePickReason(pick.reason)) return {};
    const cause =
      describeIneligiblePickDetail(pick.reason, pick.detail, t) ??
      t(`routes.buildAlert.reasons.${pick.reason}`);
    // The template supplies the full stops; a cause ending in an abbreviation
    // («на 20 окт.») must not read «окт.. Снимите».
    const trimStop = (text: string) => text.replace(/\.+$/, "");
    return {
      cause: trimStop(cause),
      advice: trimStop(t(`routes.buildAlert.suggestions.${pick.reason}`)),
      context: "single",
    };
  }

  const byReason = new Map<IneligiblePickReason, RefusedPick[]>();
  let unknown = 0;
  for (const pick of picks) {
    if (!isIneligiblePickReason(pick.reason)) {
      unknown += 1;
      continue;
    }
    const list = byReason.get(pick.reason);
    if (list) list.push(pick);
    else byReason.set(pick.reason, [pick]);
  }

  const parts: string[] = [];
  const routed = byReason.get("already_routed");
  if (routed) {
    const routes = new Map<string, string>();
    for (const pick of routed) {
      const route = readPickRoute(pick.detail);
      if (!route) continue;
      const key = String(route.id ?? route.name);
      if (!routes.has(key)) routes.set(key, pickRouteLabel(route, t));
    }
    const labels = [...routes.values()];
    const named =
      labels.length > MAX_NAMED_ROUTES
        ? `${labels.slice(0, MAX_NAMED_ROUTES).join("; ")}; …`
        : labels.join("; ");
    parts.push(
      t("buildRoute.serverErrors.ineligibleAlreadyRouted", {
        count: routed.length,
        ...(named ? { routes: named, context: "named" } : {}),
      }),
    );
  }
  const archived = byReason.get("order_archived");
  if (archived) {
    parts.push(
      t("buildRoute.serverErrors.ineligibleArchived", {
        count: archived.length,
      }),
    );
  }
  const terminal = byReason.get("order_terminal");
  if (terminal) {
    const statuses = [
      ...new Set(
        terminal
          .map((pick) => terminalStatusLabel(pick.detail, t))
          .filter((label): label is string => label !== null)
          .map((label) => label.toLocaleLowerCase()),
      ),
    ].join(", ");
    parts.push(
      t("buildRoute.serverErrors.ineligibleTerminal", {
        count: terminal.length,
        ...(statuses ? { statuses, context: "named" } : {}),
      }),
    );
  }
  const missing = byReason.get("order_missing");
  if (missing) {
    parts.push(
      t("buildRoute.serverErrors.ineligibleMissing", {
        count: missing.length,
      }),
    );
  }
  // Picks past the backend's 10-item cap (and any reason newer than this bundle).
  const rest = Math.max(0, count - picks.length) + unknown;
  if (rest > 0) {
    parts.push(t("buildRoute.serverErrors.ineligibleMore", { count: rest }));
  }
  if (parts.length === 0) return {};

  const advice: string[] = [];
  if (routed) advice.push(t("buildRoute.serverErrors.ineligibleAdviceRouted"));
  if (archived || terminal || missing || rest > 0) {
    advice.push(t("buildRoute.serverErrors.ineligibleAdviceRefresh"));
  }

  return {
    count,
    reasons: parts.join(", "),
    advice: advice.join(". "),
    context: "figures",
  };
}
